import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { FolderGit2, KeyRound, Laptop, Trash2 } from "lucide-react";
import { AgentAvatar } from "@/components/agent-avatar";
import { IconPicker } from "@/components/icon-picker";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input, Textarea } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { McpToolsField } from "@/components/mcp-tools";
import { ProjectSelect, shortPath } from "@/components/project-select";
import { PairingDialog } from "@/components/foreign-agents";
import { api, ApiError } from "@/lib/api";
import { machineName } from "@/lib/foreign-agents";
import { suggestHandle } from "@/lib/handles";
import { attachmentsOf, choicesOf, sameChoices, type McpChoice } from "@/lib/mcp-tools";
import { qk } from "@/lib/query/keys";
import type { AgentWithContext, EngineModelOption, EngineSettings, Project } from "@/lib/types";

const selectClaudeModels = (r: { settings: EngineSettings }) => r.settings.claudeModels;


/**
 * Makes or edits an agent. Picking a project is required and said plainly,
 * because it is the grant: the agent reads, edits and runs commands in that
 * directory whenever it is asked something.
 */
export function AgentDialog({
  agent,
  projects,
  open,
  onOpenChange,
  onSaved,
  onDeleted,
  onManageProjects,
  focusMcp = false,
}: {
  /** null to create one. */
  agent: AgentWithContext | null;
  projects: Project[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: (agent: AgentWithContext) => void;
  onDeleted?: (agentId: string) => void;
  onManageProjects: () => void;
  /** Open scrolled to the MCP tools section (from the chat header's dropdown). */
  focusMcp?: boolean;
}) {
  const [name, setName] = useState("");
  const [handle, setHandle] = useState("");
  const [projectId, setProjectId] = useState<string | null>(null);
  const [instructions, setInstructions] = useState("");
  const [model, setModel] = useState("");
  const [fallbackModel, setFallbackModel] = useState("");
  const [allowedTools, setAllowedTools] = useState("");
  const [mcp, setMcp] = useState<McpChoice[]>([]);
  // A server typed into "Register a server" but never registered: Save would silently drop it.
  const [mcpPending, setMcpPending] = useState(false);
  const [mcpNudge, setMcpNudge] = useState(false);
  const [contextLimit, setContextLimit] = useState("");
  const [maxParallel, setMaxParallel] = useState("1");
  const [icon, setIcon] = useState<string | null>(null);

  // Claude models seen on this host, offered as suggestions; an agent's model applies on Claude Code only.
  const claudeModels = useQuery({ queryKey: qk.engineSettings(), queryFn: () => api.engineSettings(), select: selectClaudeModels }).data;
  const [iconColor, setIconColor] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(agent?.name ?? "");
    setHandle(agent?.handle ?? "");
    setProjectId(agent?.projectId ?? null);
    setInstructions(agent?.instructions ?? "");
    setModel(agent?.model ?? "");
    setFallbackModel(agent?.fallbackModel ?? "");
    setAllowedTools(agent?.allowedTools ?? "");
    setMcp(choicesOf(agent?.mcpServers));
    setMcpNudge(false);
    setContextLimit(agent?.contextLimit ? String(Math.round(agent.contextLimit / 1000)) : "");
    setMaxParallel(String(agent?.maxParallel ?? 1));
    setIcon(agent?.icon ?? null);
    setIconColor(agent?.iconColor ?? null);
    setPicking(false);
    setError(null);
  }, [open, agent]);

  // Opened from the chat header's MCP dropdown: bring that section into view.
  useEffect(() => {
    if (!open || !focusMcp) return;
    const frame = requestAnimationFrame(() => document.getElementById("agent-mcp")?.scrollIntoView({ block: "center", behavior: "smooth" }));
    return () => cancelAnimationFrame(frame);
  }, [open, focusMcp, agent]);

  const chosen = projects.find((project) => project.id === projectId) ?? null;
  // The master works in the workspace, with no project to pick, and cannot be deleted.
  const master = agent?.kind === "master";
  // A foreign agent works on its own machine: no project here, no parallel runs, no MCP servers or fallback from this host.
  const foreign = agent?.kind === "foreign";
  const [pairing, setPairing] = useState(false);
  const moving = agent !== null && projectId !== agent.projectId;

  const submit = async () => {
    if (!projectId && !master && !foreign) {
      setError("Pick the project this agent works in.");
      return;
    }
    if (mcpPending) {
      setMcpNudge(true);
      setError("Register the MCP server first (its Register button), or cancel it.");
      document.getElementById("agent-mcp")?.scrollIntoView({ block: "end", behavior: "smooth" });
      return;
    }
    setSaving(true);
    setError(null);
    const payload = {
      name,
      handle: handle.trim() || undefined,
      instructions: instructions.trim() || null,
      model: model.trim() || null,
      ...(foreign ? {} : { fallbackModel: fallbackModel.trim() || null }),
      allowedTools: allowedTools.trim() || null,
      // Only when it changed (or a new agent picked some): the server replaces the set.
      ...(foreign || sameChoices(mcp, choicesOf(agent?.mcpServers)) ? {} : { mcpServers: attachmentsOf(mcp) }),
      contextLimit: contextLimit.trim() ? Math.round(Number(contextLimit) * 1000) : null,
      // The master works in the workspace, not a repository: it never runs in parallel.
      ...(master || foreign ? {} : { maxParallel: Math.min(Math.max(Number(maxParallel) || 1, 1), 8) }),
      // Only what was picked: left alone, a new agent's avatar is inferred by the server.
      ...(icon && icon !== agent?.icon ? { icon } : {}),
      ...(iconColor && iconColor !== agent?.iconColor ? { iconColor } : {}),
    };
    try {
      const saved = agent
        ? await api.updateAgent(agent.id, master || foreign ? payload : { ...payload, project: projectId! })
        : await api.createAgent({ ...payload, project: projectId! });
      onOpenChange(false);
      onSaved(saved);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Could not save the agent");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!agent) return;
    if (!window.confirm(`Delete ${agent.name} and its conversation? Cards and comments that name it keep its name.`)) return;
    setDeleting(true);
    try {
      await api.deleteAgent(agent.id);
      onOpenChange(false);
      onDeleted?.(agent.id);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Could not delete the agent");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{agent ? `Edit ${agent.name}` : "New agent"}</DialogTitle>
          <DialogDescription>
            {foreign ? (
              "Claude Code on its own machine, connected through board-link and signed in with that machine's Claude login. Talk to it in its chat, @mention it on a card, or assign it a card."
            ) : master ? (
              "The master agent runs the app for you — it registers projects, clones repositories into the workspace, and makes boards and agents. Its standing instructions are yours to set."
            ) : (
              <>
            An agent is one long-lived Claude Code session in a project. Talk to it in its chat, @mention it on a card, or
            assign it a card — it keeps the same conversation across all of them.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-[auto_1fr] gap-3 sm:grid-cols-[auto_1fr_1fr]">
          <div className="space-y-1.5">
            <Label>Icon</Label>
            <button
              type="button"
              aria-label="Choose the icon"
              aria-expanded={picking}
              onClick={() => setPicking((open) => !open)}
              className="block rounded-full outline-offset-2"
              title={icon ? "Change the icon" : "Picked from the name until you choose one"}
            >
              <AgentAvatar
                agent={{ name: name || "?", status: agent?.status ?? "idle", kind: agent?.kind, icon: icon ?? (agent ? null : "sparkles"), iconColor }}
                size="md"
              />
            </button>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="agent-name">Name</Label>
            <Input
              id="agent-name"
              value={name}
              placeholder="Frontend"
              autoFocus
              onChange={(event) => setName(event.target.value)}
            />
          </div>
          <div className="col-span-2 space-y-1.5 sm:col-span-1">
            <Label htmlFor="agent-handle">Handle</Label>
            <div className="relative">
              <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                @
              </span>
              <Input
                id="agent-handle"
                className="pl-6"
                value={handle}
                placeholder={suggestHandle(name) || "frontend"}
                onChange={(event) => setHandle(event.target.value.replace(/^@/, "").toLowerCase())}
              />
            </div>
          </div>
        </div>

        {picking ? (
          <IconPicker
            icon={icon}
            color={iconColor}
            onChange={(next) => {
              setIcon(next.icon);
              setIconColor(next.color);
            }}
          />
        ) : null}

        {foreign ? (
          <div className="flex flex-wrap items-center gap-2 rounded-md border border-border/70 bg-surface/50 px-3 py-2 text-[12.5px]">
            <Laptop className="size-3.5 shrink-0 text-primary" />
            <span className="min-w-0 flex-1">
              {agent!.link?.paired ? (
                <>
                  Runs on <span className="font-medium">{machineName(agent!.link)}</span>
                  {agent!.link.dir ? (
                    <>
                      {" "}
                      in <span className="font-mono text-[11.5px]">{shortPath(agent!.link.dir)}</span>
                    </>
                  ) : null}
                </>
              ) : (
                <span className="text-muted-foreground">Not paired with a machine yet.</span>
              )}
            </span>
            <Button type="button" size="xs" variant="outline" onClick={() => setPairing(true)}>
              <KeyRound /> Pairing…
            </Button>
            <PairingDialog agent={agent!} open={pairing} onOpenChange={setPairing} />
          </div>
        ) : master ? (
          <p className="flex items-center gap-1.5 font-mono text-[11px] text-muted-foreground">
            <FolderGit2 className="size-3 shrink-0 text-primary" />
            {shortPath(agent!.directory.path)} — the workspace; it clones and registers projects here.
          </p>
        ) : (
        <div className="space-y-1.5">
          <Label>Project</Label>
          <ProjectSelect projects={projects} value={projectId} onChange={setProjectId} ariaLabel="Agent project" />
          {chosen ? (
            <p className="flex items-center gap-1.5 font-mono text-[11px] text-muted-foreground">
              <FolderGit2 className="size-3 shrink-0 text-primary" />
              {shortPath(chosen.path)} — it can read, edit and run commands in here.
            </p>
          ) : projects.length === 0 ? (
            <p className="text-[11.5px] text-muted-foreground">
              No projects registered yet.{" "}
              <button className="underline underline-offset-2" onClick={onManageProjects}>
                Register one
              </button>{" "}
              first.
            </p>
          ) : null}
          {moving ? (
            <p className="text-[11.5px] text-muted-foreground">
              Moving it starts a fresh session in the new directory. Its chat history is kept.
            </p>
          ) : null}
        </div>
        )}

        <div className="space-y-1.5">
          <Label htmlFor="agent-instructions">Standing instructions</Label>
          <Textarea
            id="agent-instructions"
            rows={4}
            value={instructions}
            placeholder="You own the React app in packages/web. Run the typecheck before you say you are done."
            onChange={(event) => setInstructions(event.target.value)}
          />
        </div>

        {foreign ? null : (
          <McpToolsField value={mcp} onChange={setMcp} onPendingChange={setMcpPending} nudge={mcpPending && mcpNudge} />
        )}

        <details className="group rounded-md border border-border/70 px-3 py-2 text-[12.5px]">
          <summary className="cursor-pointer select-none text-muted-foreground group-open:mb-2">Advanced</summary>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="agent-model">Model</Label>
              <Input
                id="agent-model"
                list="engine-model-suggestions"
                value={model}
                placeholder="worker default"
                onChange={(event) => setModel(event.target.value)}
              />
              <datalist id="engine-model-suggestions">
                {claudeModels?.map((m: EngineModelOption) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </datalist>
            </div>
            {foreign ? null : (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="agent-fallback-model">Fallback model</Label>
                  <Input
                    id="agent-fallback-model"
                    list="engine-model-suggestions"
                    value={fallbackModel}
                    placeholder="none"
                    onChange={(event) => setFallbackModel(event.target.value)}
                  />
                </div>
                <p className="-mt-1 text-[11.5px] text-muted-foreground sm:col-span-2">
                  The fallback runs a turn when the model hits its usage limit, in the same session, until the limit resets, then
                  the model takes over again. It also covers an overloaded model.
                </p>
              </>
            )}
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="agent-tools">Allowed tools</Label>
              <Input
                id="agent-tools"
                value={allowedTools}
                placeholder="worker default"
                onChange={(event) => setAllowedTools(event.target.value)}
              />
            </div>
          </div>
          <div className="mt-3 space-y-1.5">
            <Label htmlFor="agent-context">Compact the context past (thousand tokens)</Label>
            <Input
              id="agent-context"
              inputMode="numeric"
              value={contextLimit}
              placeholder={agent ? String(Math.round(agent.effectiveContextLimit / 1000)) : "worker default"}
              onChange={(event) => setContextLimit(event.target.value.replace(/[^\d]/g, ""))}
            />
            <p className="text-[11px] leading-snug text-muted-foreground">
              Every turn re-sends the whole conversation. Past this, the agent summarises it and carries on from the
              summary, so a long-lived agent's cost per turn stays bounded. The chat keeps every message either way.
            </p>
          </div>
          {master || foreign ? null : (
            <div className="mt-3 space-y-1.5">
              <Label htmlFor="agent-parallel">Cards at once</Label>
              <Input
                id="agent-parallel"
                inputMode="numeric"
                value={maxParallel}
                onChange={(event) => setMaxParallel(event.target.value.replace(/[^\d]/g, "").slice(0, 1))}
                className="w-20"
              />
              <p className="text-[11px] leading-snug text-muted-foreground">
                1 to 8. Above 1, each card assigned to this agent runs in its own git worktree and branch, beside its
                main session, and the worker merges it back into main locally when the run finishes. Cards whose areas
                overlap never run together. Needs the project to be a git repository.
              </p>
            </div>
          )}
          <p className="mt-2 text-[11px] leading-snug text-muted-foreground">
            Allowed tools are space-separated, as <span className="font-mono">--allowedTools</span> takes them, e.g.{" "}
            <span className="font-mono">mcp__board Read Grep Bash(git *)</span>. Empty uses the worker's{" "}
            <span className="font-mono">AGENT_ALLOWED_TOOLS</span>.
          </p>
        </details>

        {error ? <p className="text-xs text-destructive">{error}</p> : null}

        <DialogFooter className="gap-2 sm:justify-between">
          {agent && !master ? (
            <Button variant="ghost" className="text-destructive" onClick={() => void remove()} loading={deleting}>
              <Trash2 /> Delete
            </Button>
          ) : (
            <span />
          )}
          <Button onClick={() => void submit()} loading={saving} disabled={!name.trim()}>
            {agent ? "Save" : "Create agent"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
